import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const resolveProjectTab = <Tab extends string>(
  value: string | null,
  validTabs: readonly Tab[],
  fallback: Tab
): Tab => {
  return value && validTabs.includes(value as Tab) ? (value as Tab) : fallback;
};

export const useProjectTabNavigation = <Tab extends string>(
  validTabs: readonly Tab[],
  fallback: Tab
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(() =>
    resolveProjectTab(requestedTab, validTabs, fallback)
  );

  useEffect(() => {
    if (requestedTab && validTabs.includes(requestedTab as Tab)) {
      setActiveTab(requestedTab as Tab);
    }
  }, [requestedTab, validTabs]);

  const selectTab = useCallback(
    (tab: Tab) => {
      setActiveTab(tab);
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('tab', tab);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return [activeTab, selectTab] as const;
};
